import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Trophy,
  DollarSign,
  Instagram,
  Users,
  Crown,
  Gift,
  CheckCircle,
  XCircle,
  Clock,
  Search,
  RefreshCw,
  Eye,
  Award,
  Banknote,
  Calendar,
  MapPin,
  Send,
  AlertCircle
} from "lucide-react";
import { API } from "@/App";
import axios from "axios";
import { toast } from "sonner";

const LookFestAdmin = () => {
  const [entries, setEntries] = useState([]);
  const [winners, setWinners] = useState([]);
  const [finderBonuses, setFinderBonuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [showWinnerModal, setShowWinnerModal] = useState(false);
  const [showBonusModal, setShowBonusModal] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState("");
  const [prizeAmount, setPrizeAmount] = useState("100");
  const [winnerNotes, setWinnerNotes] = useState("");

  const months = [
    "February 2026", "March 2026", "April 2026", "May 2026",
    "June 2026", "July 2026", "August 2026", "September 2026", "October 2026 (Finale)"
  ];

  const stats = {
    totalEntries: entries.length,
    pendingVerification: entries.filter(e => e.verification_status === "pending").length,
    verified: entries.filter(e => e.verification_status === "verified").length,
    totalWinners: winners.length,
    pendingBonuses: finderBonuses.filter(b => b.status === "pending").length,
    paidBonuses: finderBonuses.filter(b => b.status === "paid").length
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [entriesRes, winnersRes, bonusesRes] = await Promise.all([
        axios.get(`${API}/admin/lookfest/entries`),
        axios.get(`${API}/admin/lookfest/winners`),
        axios.get(`${API}/admin/lookfest/finder-bonuses`)
      ]);
      setEntries(entriesRes.data.entries || []);
      setWinners(winnersRes.data.winners || []);
      setFinderBonuses(bonusesRes.data.bonuses || []);
    } catch (error) {
      console.error("Error fetching Look Fest data:", error);
      // Use empty arrays if endpoints don't exist yet
      setEntries([]);
      setWinners([]);
      setFinderBonuses([]);
    }
    setLoading(false);
  };

  const verifyEntry = async (entryId, status) => {
    try {
      await axios.post(`${API}/admin/lookfest/verify`, {
        entry_id: entryId,
        status: status
      });
      toast.success(`Entry ${status === "verified" ? "verified" : "rejected"}`);
      fetchData();
    } catch (error) {
      toast.error("Failed to update verification status");
    }
  };

  const selectWinner = async () => {
    if (!selectedEntry || !selectedMonth) {
      toast.error("Please select an entry and month");
      return;
    }

    try {
      await axios.post(`${API}/admin/lookfest/select-winner`, {
        entry_id: selectedEntry.entry_id,
        month: selectedMonth,
        prize_amount: parseFloat(prizeAmount),
        notes: winnerNotes
      });
      toast.success(`Winner selected for ${selectedMonth}!`);
      setShowWinnerModal(false);
      setSelectedEntry(null);
      setSelectedMonth("");
      setWinnerNotes("");
      fetchData();
    } catch (error) {
      toast.error("Failed to select winner");
    }
  };

  const payFinderBonus = async (bonusId) => {
    try {
      await axios.post(`${API}/admin/lookfest/pay-finder-bonus`, {
        bonus_id: bonusId
      });
      toast.success("Finder bonus marked as paid!");
      fetchData();
    } catch (error) {
      toast.error("Failed to process finder bonus");
    }
  };

  const filteredEntries = entries.filter(entry => {
    const matchesSearch = 
      entry.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      entry.instagram_handle?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      entry.shop_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      entry.email?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesFilter = filterStatus === "all" || entry.verification_status === filterStatus;
    
    return matchesSearch && matchesFilter;
  });

  return (
    <div className="space-y-6">
      {/* Stats Overview */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <Card className="bg-gradient-to-br from-amber-500/20 to-orange-500/20 border-amber-500/30">
          <CardContent className="p-4 text-center">
            <Trophy className="w-8 h-8 text-amber-400 mx-auto mb-2" />
            <p className="text-2xl font-bold text-white">{stats.totalEntries}</p>
            <p className="text-xs text-gray-400">Total Entries</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-blue-500/20 to-indigo-500/20 border-blue-500/30">
          <CardContent className="p-4 text-center">
            <Clock className="w-8 h-8 text-blue-400 mx-auto mb-2" />
            <p className="text-2xl font-bold text-white">{stats.pendingVerification}</p>
            <p className="text-xs text-gray-400">Pending Review</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-green-500/20 to-emerald-500/20 border-green-500/30">
          <CardContent className="p-4 text-center">
            <CheckCircle className="w-8 h-8 text-green-400 mx-auto mb-2" />
            <p className="text-2xl font-bold text-white">{stats.verified}</p>
            <p className="text-xs text-gray-400">Verified</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-purple-500/20 to-pink-500/20 border-purple-500/30">
          <CardContent className="p-4 text-center">
            <Crown className="w-8 h-8 text-purple-400 mx-auto mb-2" />
            <p className="text-2xl font-bold text-white">{stats.totalWinners}</p>
            <p className="text-xs text-gray-400">Winners</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-yellow-500/20 to-amber-500/20 border-yellow-500/30">
          <CardContent className="p-4 text-center">
            <Gift className="w-8 h-8 text-yellow-400 mx-auto mb-2" />
            <p className="text-2xl font-bold text-white">{stats.pendingBonuses}</p>
            <p className="text-xs text-gray-400">Pending Bonuses</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-green-500/20 to-teal-500/20 border-green-500/30">
          <CardContent className="p-4 text-center">
            <Banknote className="w-8 h-8 text-green-400 mx-auto mb-2" />
            <p className="text-2xl font-bold text-white">${stats.paidBonuses * 25}</p>
            <p className="text-xs text-gray-400">Bonuses Paid</p>
          </CardContent>
        </Card>
      </div>

      {/* Finder Bonus Section */}
      <Card className="bg-gradient-to-br from-purple-900/30 to-indigo-900/30 border-purple-500/30">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-white">
            <Gift className="w-5 h-5 text-purple-400" />
            $25 Finder Bonuses (Soldier Referrals)
          </CardTitle>
        </CardHeader>
        <CardContent>
          {finderBonuses.length === 0 ? (
            <div className="text-center py-8 text-gray-400">
              <Gift className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No finder bonuses pending</p>
              <p className="text-sm">When a referred contestant wins, their Soldier earns $25</p>
            </div>
          ) : (
            <div className="space-y-3">
              {finderBonuses.map((bonus) => (
                <div key={bonus.bonus_id} className="flex items-center justify-between bg-stone-800/50 rounded-lg p-4">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 bg-purple-500/20 rounded-full flex items-center justify-center">
                      <Users className="w-5 h-5 text-purple-400" />
                    </div>
                    <div>
                      <p className="font-medium text-white">{bonus.soldier_name}</p>
                      <p className="text-sm text-gray-400">Referred: {bonus.winner_name}</p>
                      <p className="text-xs text-gray-500">Scout Code: {bonus.scout_code}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <p className="text-xl font-bold text-green-400">$25</p>
                      <p className="text-xs text-gray-400">{bonus.month}</p>
                    </div>
                    {bonus.status === "pending" ? (
                      <Button
                        size="sm"
                        className="bg-green-600 hover:bg-green-700"
                        onClick={() => payFinderBonus(bonus.bonus_id)}
                      >
                        <Banknote className="w-4 h-4 mr-1" />
                        Pay Now
                      </Button>
                    ) : (
                      <Badge className="bg-green-500/20 text-green-400 border-green-500/30">
                        <CheckCircle className="w-3 h-3 mr-1" />
                        Paid
                      </Badge>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Winners Section */}
      <Card className="bg-stone-800/50 border-stone-700">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-white">
            <Crown className="w-5 h-5 text-amber-400" />
            Monthly Winners
          </CardTitle>
        </CardHeader>
        <CardContent>
          {winners.length === 0 ? (
            <div className="text-center py-8 text-gray-400">
              <Trophy className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No winners selected yet</p>
              <p className="text-sm">Select winners from verified entries below</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {winners.map((winner) => (
                <Card key={winner.winner_id} className="bg-gradient-to-br from-amber-500/10 to-orange-500/10 border-amber-500/30">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-3">
                      <Badge className="bg-amber-500 text-black">{winner.month}</Badge>
                      <span className="text-xl font-bold text-green-400">${winner.prize_amount}</span>
                    </div>
                    <p className="font-bold text-white">{winner.name}</p>
                    <p className="text-sm text-amber-400">{winner.shop_name}</p>
                    <p className="text-xs text-gray-400 flex items-center gap-1 mt-1">
                      <Instagram className="w-3 h-3" /> {winner.instagram_handle}
                    </p>
                    <p className="text-xs text-gray-500 flex items-center gap-1">
                      <MapPin className="w-3 h-3" /> {winner.shop_location}
                    </p>
                    {winner.scout_code && (
                      <p className="text-xs text-purple-400 mt-2">
                        <Gift className="w-3 h-3 inline mr-1" />
                        Referred by Scout: {winner.scout_code}
                      </p>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Entries Management */}
      <Card className="bg-stone-800/50 border-stone-700">
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <CardTitle className="flex items-center gap-2 text-white">
              <Trophy className="w-5 h-5 text-amber-400" />
              Contest Entries
            </CardTitle>
            <div className="flex items-center gap-3">
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <Input
                  placeholder="Search entries..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9 bg-stone-700 border-stone-600 w-48"
                />
              </div>
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="w-36 bg-stone-700 border-stone-600">
                  <SelectValue placeholder="Filter" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="verified">Verified</SelectItem>
                  <SelectItem value="rejected">Rejected</SelectItem>
                </SelectContent>
              </Select>
              <Button variant="outline" size="sm" onClick={fetchData}>
                <RefreshCw className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-12">
              <RefreshCw className="w-8 h-8 animate-spin mx-auto text-amber-400" />
              <p className="text-gray-400 mt-2">Loading entries...</p>
            </div>
          ) : filteredEntries.length === 0 ? (
            <div className="text-center py-12 text-gray-400">
              <Trophy className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No contest entries yet</p>
              <p className="text-sm">Entries will appear here when stylists register for Look Fest</p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredEntries.map((entry) => (
                <div key={entry.entry_id} className="bg-stone-700/50 rounded-lg p-4">
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-amber-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                        <Trophy className="w-6 h-6 text-amber-400" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="font-bold text-white">{entry.name}</p>
                          <Badge className={
                            entry.verification_status === "verified" ? "bg-green-500/20 text-green-400 border-green-500/30" :
                            entry.verification_status === "rejected" ? "bg-red-500/20 text-red-400 border-red-500/30" :
                            "bg-blue-500/20 text-blue-400 border-blue-500/30"
                          }>
                            {entry.verification_status || "pending"}
                          </Badge>
                        </div>
                        <p className="text-sm text-amber-400">{entry.shop_name}</p>
                        <div className="flex flex-wrap items-center gap-3 text-xs text-gray-400 mt-1">
                          <span className="flex items-center gap-1">
                            <Instagram className="w-3 h-3 text-pink-400" /> {entry.instagram_handle}
                          </span>
                          <span className="flex items-center gap-1">
                            <MapPin className="w-3 h-3" /> {entry.shop_location}
                          </span>
                          <span>{entry.email}</span>
                        </div>
                        {entry.scout_code && (
                          <p className="text-xs text-purple-400 mt-1">
                            <Gift className="w-3 h-3 inline mr-1" />
                            Scout Code: {entry.scout_code}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {entry.verification_status === "pending" && (
                        <>
                          <Button
                            size="sm"
                            className="bg-green-600 hover:bg-green-700"
                            onClick={() => verifyEntry(entry.entry_id, "verified")}
                          >
                            <CheckCircle className="w-4 h-4 mr-1" />
                            Verify
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                            onClick={() => verifyEntry(entry.entry_id, "rejected")}
                          >
                            <XCircle className="w-4 h-4 mr-1" />
                            Reject
                          </Button>
                        </>
                      )}
                      {entry.verification_status === "verified" && (
                        <Button
                          size="sm"
                          className="bg-amber-500 hover:bg-amber-600 text-black"
                          onClick={() => {
                            setSelectedEntry(entry);
                            setShowWinnerModal(true);
                          }}
                        >
                          <Crown className="w-4 h-4 mr-1" />
                          Select Winner
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Select Winner Modal */}
      <Dialog open={showWinnerModal} onOpenChange={setShowWinnerModal}>
        <DialogContent className="bg-stone-900 border-stone-700 text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Crown className="w-5 h-5 text-amber-400" />
              Select Monthly Winner
            </DialogTitle>
          </DialogHeader>
          {selectedEntry && (
            <div className="space-y-4">
              <div className="bg-stone-800 rounded-lg p-4">
                <p className="font-bold text-lg">{selectedEntry.name}</p>
                <p className="text-amber-400">{selectedEntry.shop_name}</p>
                <p className="text-sm text-gray-400">{selectedEntry.instagram_handle}</p>
              </div>

              <div className="space-y-2">
                <Label>Select Month</Label>
                <Select value={selectedMonth} onValueChange={setSelectedMonth}>
                  <SelectTrigger className="bg-stone-800 border-stone-600">
                    <SelectValue placeholder="Choose month" />
                  </SelectTrigger>
                  <SelectContent>
                    {months.map((month) => (
                      <SelectItem key={month} value={month}>{month}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Prize Amount ($)</Label>
                <Input
                  type="number"
                  value={prizeAmount}
                  onChange={(e) => setPrizeAmount(e.target.value)}
                  className="bg-stone-800 border-stone-600"
                />
              </div>

              <div className="space-y-2">
                <Label>Notes (optional)</Label>
                <Textarea
                  value={winnerNotes}
                  onChange={(e) => setWinnerNotes(e.target.value)}
                  placeholder="Any notes about this winner..."
                  className="bg-stone-800 border-stone-600"
                />
              </div>

              {selectedEntry.scout_code && (
                <div className="bg-purple-500/20 border border-purple-500/30 rounded-lg p-3">
                  <p className="text-purple-300 text-sm flex items-center gap-2">
                    <Gift className="w-4 h-4" />
                    This entry has Scout Code: <strong>{selectedEntry.scout_code}</strong>
                  </p>
                  <p className="text-purple-400 text-sm mt-1">
                    A $25 Finder Bonus will be created for the referring Soldier
                  </p>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowWinnerModal(false)}>
              Cancel
            </Button>
            <Button className="bg-amber-500 hover:bg-amber-600 text-black" onClick={selectWinner}>
              <Trophy className="w-4 h-4 mr-2" />
              Confirm Winner
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LookFestAdmin;
